/// Zustand store for all AI state: configuration, conversations, streaming,
/// model list, and settings dialog lifecycle.
///
/// Persists API key, default model, consent, and context window size in
/// localStorage. API key is only stored after explicit user consent.

import { create } from "zustand";
import type {
  ChatMessage,
  Conversation,
  OpenRouterModel,
  GenerationResult,
  DiagnosisRequest,
} from "@/types/ai";
import { fetchModels } from "@/services/openrouter/models";
import { streamChatCompletion } from "@/services/openrouter/client";
import { ERROR_DIAGNOSIS_SYSTEM_PROMPT } from "@/services/openrouter/prompts";

// ---------------------------------------------------------------------------
// localStorage keys
// ---------------------------------------------------------------------------

const LS_API_KEY = "flowy-ai-apikey";
const LS_DEFAULT_MODEL = "flowy-ai-default-model";
const LS_CONSENT = "flowy-ai-consent";
const LS_CONTEXT_WINDOW = "flowy-ai-context-window";

// ---------------------------------------------------------------------------
// Cache duration
// ---------------------------------------------------------------------------

/** Model list cache TTL: 1 hour in milliseconds. */
const MODEL_CACHE_TTL = 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

const DEFAULT_MODEL = "nvidia/nemotron-3-nano-30b-a3b:free";
const DEFAULT_CONTEXT_WINDOW = 20;

// ---------------------------------------------------------------------------
// Store state shape
// ---------------------------------------------------------------------------

interface AIStoreState {
  /** OpenRouter API key (null if not set or consent not given). */
  apiKey: string | null;
  /** Default model for new conversations. */
  defaultModel: string;
  /** Currently selected model for the next message (starts as defaultModel). */
  selectedModel: string;
  /** Cached model list from OpenRouter. */
  models: OpenRouterModel[];
  /** Timestamp of last model list fetch for cache invalidation. */
  modelsLastFetched: number;
  /** All active conversations, keyed by editor tab ID. */
  conversations: Map<string, Conversation>;
  /** Whether an AI streaming request is in progress. */
  isStreaming: boolean;
  /** Tab ID currently receiving a streaming response. */
  streamingTabId: string | null;
  /** AbortController for the current streaming request. */
  abortController: AbortController | null;
  /** Whether the AI settings dialog is visible. */
  settingsOpen: boolean;
  /** Maximum number of messages to include in LLM context. */
  contextWindowSize: number;
  /** Whether the user has consented to localStorage storage. */
  consentGiven: boolean;

  /** Generation results keyed by tab ID, for iterative refinement. */
  generationResults: Map<string, GenerationResult>;
  /** Tab ID of the last AI-generated diagram (for refinement targeting). */
  lastGenerationTabId: string | null;

  /** Pending diagnosis request currently being streamed. */
  pendingDiagnosis: DiagnosisRequest | null;
  /** Whether automatic error diagnosis is enabled. */
  autoDiagnoseEnabled: boolean;
}

interface AIStoreActions {
  // Configuration
  setApiKey: (key: string | null) => void;
  clearApiKey: () => void;
  giveConsent: () => void;
  setDefaultModel: (modelId: string) => void;
  setSelectedModel: (modelId: string) => void;
  setContextWindowSize: (size: number) => void;

  // Model list
  loadModels: () => Promise<void>;

  // Settings dialog
  openSettings: () => void;
  closeSettings: () => void;

  // Conversations
  getConversation: (tabId: string) => Conversation;
  addMessage: (tabId: string, message: ChatMessage) => void;
  updateLastAssistantMessage: (tabId: string, content: string) => void;
  clearConversation: (tabId: string) => void;

  // Streaming lifecycle
  setStreaming: (tabId: string | null) => void;
  abortStreaming: () => void;

  // Generation tracking
  setGenerationResult: (tabId: string, result: GenerationResult) => void;
  getGenerationResult: (tabId: string) => GenerationResult | undefined;

  // Diagnosis
  triggerDiagnosis: (request: DiagnosisRequest) => Promise<void>;
  setAutoDiagnoseEnabled: (enabled: boolean) => void;
}

type AIStore = AIStoreState & AIStoreActions;

// ---------------------------------------------------------------------------
// Helpers: safe localStorage access
// ---------------------------------------------------------------------------

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage may be full or blocked
  }
}

function lsRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore errors
  }
}

// ---------------------------------------------------------------------------
// Initial state from localStorage
// ---------------------------------------------------------------------------

function loadInitialState(): Pick<
  AIStoreState,
  "apiKey" | "defaultModel" | "selectedModel" | "consentGiven" | "contextWindowSize"
> {
  const consent = lsGet(LS_CONSENT) === "true";
  const savedKey = consent ? lsGet(LS_API_KEY) : null;
  const savedModel = lsGet(LS_DEFAULT_MODEL) ?? DEFAULT_MODEL;
  const savedWindow = parseInt(lsGet(LS_CONTEXT_WINDOW) ?? "", 10);
  const contextWindowSize = Number.isFinite(savedWindow) ? savedWindow : DEFAULT_CONTEXT_WINDOW;

  return {
    apiKey: savedKey,
    defaultModel: savedModel,
    selectedModel: savedModel,
    consentGiven: consent,
    contextWindowSize,
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAIStore = create<AIStore>((set, get) => {
  const initial = loadInitialState();

  return {
    // State
    apiKey: initial.apiKey,
    defaultModel: initial.defaultModel,
    selectedModel: initial.selectedModel,
    models: [],
    modelsLastFetched: 0,
    conversations: new Map(),
    isStreaming: false,
    streamingTabId: null,
    abortController: null,
    settingsOpen: false,
    contextWindowSize: initial.contextWindowSize,
    consentGiven: initial.consentGiven,
    generationResults: new Map(),
    lastGenerationTabId: null,
    pendingDiagnosis: null,
    autoDiagnoseEnabled: true,

    // -----------------------------------------------------------------------
    // Configuration
    // -----------------------------------------------------------------------

    setApiKey: (key) => {
      const state = get();
      if (state.consentGiven && key !== null) {
        lsSet(LS_API_KEY, key);
      }
      set({ apiKey: key });
    },

    clearApiKey: () => {
      lsRemove(LS_API_KEY);
      set({ apiKey: null });
    },

    giveConsent: () => {
      lsSet(LS_CONSENT, "true");
      set({ consentGiven: true });
    },

    setDefaultModel: (modelId) => {
      lsSet(LS_DEFAULT_MODEL, modelId);
      set({ defaultModel: modelId, selectedModel: modelId });
    },

    setSelectedModel: (modelId) => {
      set({ selectedModel: modelId });
    },

    setContextWindowSize: (size) => {
      const clamped = Math.max(5, Math.min(50, size));
      lsSet(LS_CONTEXT_WINDOW, String(clamped));
      set({ contextWindowSize: clamped });
    },

    // -----------------------------------------------------------------------
    // Model list
    // -----------------------------------------------------------------------

    loadModels: async () => {
      const state = get();
      if (!state.apiKey) return;

      // Check cache validity
      const now = Date.now();
      if (state.models.length > 0 && now - state.modelsLastFetched < MODEL_CACHE_TTL) {
        return;
      }

      try {
        const models = await fetchModels(state.apiKey);
        set({ models, modelsLastFetched: now });
      } catch (error) {
        console.error("Failed to load models:", error);
      }
    },

    // -----------------------------------------------------------------------
    // Settings dialog
    // -----------------------------------------------------------------------

    openSettings: () => set({ settingsOpen: true }),
    closeSettings: () => set({ settingsOpen: false }),

    // -----------------------------------------------------------------------
    // Conversations
    // -----------------------------------------------------------------------

    getConversation: (tabId) => {
      const state = get();
      const existing = state.conversations.get(tabId);
      if (existing) return existing;

      const newConversation: Conversation = {
        id: `conv-${tabId}-${Date.now()}`,
        tabId,
        messages: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      const conversations = new Map(state.conversations);
      conversations.set(tabId, newConversation);
      set({ conversations });
      return newConversation;
    },

    addMessage: (tabId, message) => {
      set((state) => {
        const conversations = new Map(state.conversations);
        const conversation = conversations.get(tabId) ?? {
          id: `conv-${tabId}-${Date.now()}`,
          tabId,
          messages: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };

        conversations.set(tabId, {
          ...conversation,
          messages: [...conversation.messages, message],
          updatedAt: Date.now(),
        });

        return { conversations };
      });
    },

    updateLastAssistantMessage: (tabId, content) => {
      set((state) => {
        const conversations = new Map(state.conversations);
        const conversation = conversations.get(tabId);
        if (!conversation || conversation.messages.length === 0) return state;

        const messages = [...conversation.messages];
        const lastIdx = messages.length - 1;
        if (messages[lastIdx].role !== "assistant") return state;

        messages[lastIdx] = { ...messages[lastIdx], content };
        conversations.set(tabId, {
          ...conversation,
          messages,
          updatedAt: Date.now(),
        });

        return { conversations };
      });
    },

    clearConversation: (tabId) => {
      set((state) => {
        const conversations = new Map(state.conversations);
        conversations.delete(tabId);
        return { conversations };
      });
    },

    // -----------------------------------------------------------------------
    // Streaming lifecycle
    // -----------------------------------------------------------------------

    setStreaming: (tabId) => {
      if (tabId) {
        const controller = new AbortController();
        set({
          isStreaming: true,
          streamingTabId: tabId,
          abortController: controller,
        });
      } else {
        set({
          isStreaming: false,
          streamingTabId: null,
          abortController: null,
        });
      }
    },

    abortStreaming: () => {
      const state = get();
      state.abortController?.abort();
      set({
        isStreaming: false,
        streamingTabId: null,
        abortController: null,
      });
    },

    // -----------------------------------------------------------------------
    // Generation tracking
    // -----------------------------------------------------------------------

    setGenerationResult: (tabId, result) => {
      set((state) => {
        const generationResults = new Map(state.generationResults);
        generationResults.set(tabId, result);
        return { generationResults, lastGenerationTabId: tabId };
      });
    },

    getGenerationResult: (tabId) => {
      return get().generationResults.get(tabId);
    },

    // -----------------------------------------------------------------------
    // Diagnosis
    // -----------------------------------------------------------------------

    triggerDiagnosis: async (request) => {
      const state = get();

      // Skip silently if no API key (don't interrupt user workflow)
      if (!state.apiKey) return;

      // Prevent overlapping diagnosis streams
      if (state.isStreaming) return;

      set({ pendingDiagnosis: request });

      const { tabId, errorType, errorMessage, elementId, diagramContent, diagramType } = request;

      // Ensure conversation exists
      state.getConversation(tabId);

      // Add system message indicating auto-diagnosis
      const systemMsg: ChatMessage = {
        role: "system",
        content: `[Auto-diagnosis] An error was detected and AI is analyzing it.`,
        timestamp: Date.now(),
        isDiagnosis: true,
        diagnosisTabId: tabId,
      };
      state.addMessage(tabId, systemMsg);

      // Add user message with error context
      const userMsg: ChatMessage = {
        role: "user",
        content: `Error occurred: ${errorType} - ${errorMessage}${elementId ? ` on element ${elementId}` : ""}`,
        timestamp: Date.now(),
      };
      state.addMessage(tabId, userMsg);

      // Build LLM messages
      const llmMessages: ChatMessage[] = [
        {
          role: "system",
          content: ERROR_DIAGNOSIS_SYSTEM_PROMPT,
          timestamp: Date.now(),
        },
      ];

      // Add diagram context
      if (diagramContent) {
        const lang = diagramType === "bpmn" ? "xml" : "json";
        llmMessages.push({
          role: "system",
          content: `Current diagram (${diagramType}):\n\`\`\`${lang}\n${diagramContent}\n\`\`\``,
          timestamp: Date.now(),
        });
      }

      // Add error details
      llmMessages.push({
        role: "user",
        content: `Error type: ${errorType}\nError message: ${errorMessage}${elementId ? `\nElement ID: ${elementId}` : ""}`,
        timestamp: Date.now(),
      });

      // Create empty assistant message for streaming
      const assistantMsg: ChatMessage = {
        role: "assistant",
        content: "",
        timestamp: Date.now(),
        model: state.selectedModel,
        isDiagnosis: true,
        diagnosisTabId: tabId,
      };
      state.addMessage(tabId, assistantMsg);

      // Set streaming state
      state.setStreaming(tabId);
      const abortController = get().abortController;

      try {
        let accumulated = "";
        const stream = streamChatCompletion(
          state.apiKey ?? "",
          state.selectedModel,
          llmMessages,
          abortController?.signal,
        );

        for await (const delta of stream) {
          accumulated += delta;
          get().updateLastAssistantMessage(tabId, accumulated);
        }

        get().setStreaming(null);
      } catch (error) {
        get().setStreaming(null);

        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        console.error("Auto-diagnosis failed:", error);
      } finally {
        set({ pendingDiagnosis: null });
      }
    },

    setAutoDiagnoseEnabled: (enabled) => {
      set({ autoDiagnoseEnabled: enabled });
    },
  };
});

// ---------------------------------------------------------------------------
// Utility: ensure API key is available
// ---------------------------------------------------------------------------

/**
 * Check if an API key is configured. If not, open the settings dialog.
 * Returns true if the key is available, false if the user needs to configure it.
 *
 * Usage: call before any AI action. If returns false, wait for settings to close
 * then check again.
 */
export function ensureAPIKey(): boolean {
  const state = useAIStore.getState();
  if (state.apiKey) return true;
  state.openSettings();
  return false;
}
